/**
 * 主题图片 URL 解析器
 *
 * 使用 Vite 的 import.meta.glob 在构建时静态分析图片资源，
 * 获取处理后的 URL（带 hash），避免运行时调用 getComputedStyle。
 *
 * 优势：
 * - 零 DOM 操作，纯 JS 对象查找
 * - Vite 自动处理图片（hash、压缩等）
 * - 新增图片自动识别，无需手动生成配置
 * - 与 SCSS 引用同一资源时自动去重
 */

// Vite 在构建时静态分析这行代码，展开为具体的文件映射
// eager: true - 立即加载（非懒加载）
// import: 'default' - 获取默认导出
// query: '?url' - 只获取 URL 字符串，不加载文件内容
const allImages = import.meta.glob<string>(
  '/src/assets/theme/**/*.{png,jpg,jpeg,svg,gif,webp}',
  {
    eager: true,
    import: 'default',
    query: '?url',
  }
);

/**
 * 主题图片映射类型
 * { [themeId]: { [page]: { [filename]: resolvedUrl } } }
 */
type ThemeImageMap = Record<string, Record<string, Record<string, string>>>;

/**
 * 构建主题图片映射表
 * 解析路径结构：/src/assets/theme/{themeId}/{page}/{filename}.ext
 */
function buildImageMap(): ThemeImageMap {
  const result: ThemeImageMap = {};

  for (const [path, url] of Object.entries(allImages)) {
    // 匹配路径：/src/assets/theme/tg_green/Index/index_logo_top.png
    const match = path.match(/\/src\/assets\/theme\/([^/]+)\/([^/]+)\/([^/]+)\.\w+$/);
    if (!match) continue;

    const [, themeId, page, filename] = match;

    // 初始化嵌套对象
    result[themeId] ??= {};
    result[themeId][page] ??= {};
    result[themeId][page][filename] = url;
  }

  return result;
}

/**
 * 解析后的主题图片映射
 * 构建时静态分析，运行时只执行一次 buildImageMap()
 */
export const resolvedThemeImages = buildImageMap();

/**
 * 调试用：打印所有已解析的图片路径
 */
export function debugPrintImages() {
  console.log('[Theme Images] Resolved:', resolvedThemeImages);
}
